import React from 'react'
import { useTranslation } from 'react-i18next'

import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'

import { useContextHook } from '../store/context'
import ProfileLibraryCard from '../components/profileLibrary/profileLibraryCard'
import { get, setPageTitle } from '../utils/service'
import { ProfileType } from '../types/enums/profileType'
import { Profile } from '../types/profile'
import { ProfileMetadataFilter } from '../types/profileMetadataFilter'
import { PagedResponse } from '../types/pagedResponse'
import Alert from '../components/alert/alert'
import Pagination from '../components/fields/pagination'
import ProfileLibraryFilters from '../components/profileLibrary/profileLibraryFilters'
import { convertProfileToDisplayUnits } from '../utils/profileEditorUtils'
import MachineLoader from '../components/navbar/machineLoader'

const ProfileLibrary = (): JSX.Element => {
  const { t } = useTranslation()
  const { state } = useContextHook()

  const [paginationSettings, setPaginationSettings] = React.useState<PagedResponse<Profile> | null>(null)
  const [hasLoadProfilesError, setHasLoadProfilesError] = React.useState<boolean>(false)
  const [loadProfilesUrl, setLoadProfilesUrl] = React.useState<string>('')
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [filterConfig, setFilterConfig] = React.useState<Array<ProfileMetadataFilter>>([])

  React.useEffect(() => {
    setPageTitle(t('profilelibrary.profilelibrary'))
  }, [t])

  React.useEffect(() => {
    setIsLoading(true)
    Promise.all([
      get<Array<ProfileMetadataFilter>>('profiles/metadata_filters/'),
      get<PagedResponse<Profile>>('profiles/library/'),
    ])
      .then(([configResponse, profilesResponse]) => {
        setFilterConfig(configResponse.data)
        setPaginationSettings(profilesResponse.data)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
        setHasLoadProfilesError(true)
      })
  }, [])

  React.useEffect(() => {
    if (loadProfilesUrl) {
      setIsLoading(true)
      get<PagedResponse<Profile>>(loadProfilesUrl)
        .then((response) => {
          setIsLoading(false)
          setPaginationSettings(response.data)
        })
        .catch(() => {
          setIsLoading(false)
          setHasLoadProfilesError(true)
        })
    }
  }, [loadProfilesUrl])

  const loadProfiles = (
    searchFilters: Array<number>,
    profileTypeFilter: Array<ProfileType>,
    weightFilter: Array<number>,
    searchTerm: string
  ): void => {
    const filterParams = searchFilters.map((x) => {
      const filter = filterConfig.find((y) => y.filter_values.find((z) => z.id === x))
      return `filter_${filter?.filter_name}=${x}`
    })
    const queryParams = [
      ...filterParams,
      ...profileTypeFilter.map((x) => `profile_type=${x}`),
      ...weightFilter.map((x, index) => (index === 0 ? `min_weight=${x}` : `max_weight=${x}`)),
      searchTerm ? `search=${searchTerm}` : '',
    ]
      .filter(Boolean)
      .join('&')

    const paramString = queryParams ? `?${queryParams}` : ''
    const requestString = `profiles/library/${paramString}`
    if (requestString !== loadProfilesUrl) {
      setPaginationSettings(null)
      setLoadProfilesUrl(requestString)
    }
  }

  return (
    <Container component="main" maxWidth={false}>
      <Typography sx={{ mb: 2 }} variant="h4">
        {t('profilelibrary.roestpublicprofilelibrary')}
      </Typography>
      <Typography variant="body2">{t('profilelibrary.libraryhelptext')}</Typography>
      <ProfileLibraryFilters filterConfig={filterConfig} onFiltersChanged={loadProfiles} />
      {isLoading && <CircularProgress sx={{ mt: 2 }} />}
      <MachineLoader />
      {!isLoading && hasLoadProfilesError && (
        <Stack sx={{ mt: 2 }}>
          <Alert severity="error">{t('profilelibrary.loadprofileserror')}</Alert>
        </Stack>
      )}
      {!isLoading && paginationSettings?.results && paginationSettings.results.length === 0 && (
        <Stack sx={{ mt: 2 }}>
          <Alert severity="info">{t('profilelibrary.noprofiles')}</Alert>
        </Stack>
      )}
      {!isLoading && paginationSettings?.results && paginationSettings.results.length > 0 && paginationSettings && (
        <>
          <Typography variant="caption">
            {t('profilelibrary.showingprofiles').replace('{0}', `${paginationSettings?.count || 0}`)}
          </Typography>
          <Stack spacing={2} sx={{ mt: 3 }}>
            {paginationSettings.results.map((x) => {
              return (
                <ProfileLibraryCard
                  key={x.id}
                  profile={convertProfileToDisplayUnits(x)}
                  filterConfig={filterConfig}
                  customerUrl={state.currentCustomer?.url || ''}
                  selectedMachineSlug={
                    state.currentMachine?.slug || (state.machines.length > 0 ? state.machines[0].slug : '')
                  }
                />
              )
            })}
          </Stack>
        </>
      )}
      <Stack sx={{ mt: 1, mb: 4 }}>
        {paginationSettings && (
          <Pagination
            isLoading={isLoading}
            paginationSettings={paginationSettings}
            onChangePage={(newPageUrl: string) => setLoadProfilesUrl(newPageUrl)}
          />
        )}
      </Stack>
    </Container>
  )
}

export default ProfileLibrary
